import { useState, useRef, useCallback } from 'react';
import Papa from 'papaparse';
import { Upload, FileText, AlertCircle } from 'lucide-react';
import { STRINGS } from './constants';
import type { ParsedCsvData } from './types';

interface StepUploadProps {
  onParsed: (data: ParsedCsvData) => void;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export default function StepUpload({ onParsed }: StepUploadProps) {
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ParsedCsvData | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const processFile = useCallback((file: File) => {
    setError(null);
    setPreview(null);

    if (!file.name.endsWith('.csv')) {
      setError(STRINGS.invalidFile);
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError(STRINGS.fileTooLarge);
      return;
    }

    setFileName(file.name);

    Papa.parse(file, {
      skipEmptyLines: true,
      complete: (results) => {
        const rows = results.data as string[][];
        if (rows.length < 2) {
          setError(STRINGS.emptyFile);
          return;
        }

        const headers = rows[0];
        const dataRows = rows.slice(1);

        setPreview({ headers, rows: dataRows });
      },
      error: () => {
        setError(STRINGS.invalidFile);
      },
    });
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) processFile(file);
    },
    [processFile],
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) processFile(file);
    },
    [processFile],
  );

  const handleContinue = () => {
    if (preview) onParsed(preview);
  };

  return (
    <div className="space-y-6">
      <div
        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
          isDragging
            ? 'border-purple-500 bg-purple-50'
            : 'border-gray-300 hover:border-purple-400 hover:bg-gray-50'
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <Upload className="h-10 w-10 mx-auto text-gray-400 mb-3" />
        <p className="text-sm font-medium text-gray-700">{STRINGS.uploadDesc}</p>
        <p className="text-xs text-gray-500 mt-1">{STRINGS.uploadAccept}</p>
        {fileName && (
          <div className="mt-3 flex items-center justify-center gap-2 text-sm text-purple-600">
            <FileText className="h-4 w-4" />
            <span>{fileName}</span>
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          className="hidden"
          onChange={handleFileChange}
        />
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {preview && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            {STRINGS.previewTitle} ({preview.rows.length} rreshta)
          </h4>
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="bg-gray-50">
                  {preview.headers.map((h, i) => (
                    <th key={i} className="px-3 py-2 text-left font-medium text-gray-600">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.rows.slice(0, 5).map((row, ri) => (
                  <tr key={ri} className="border-t border-gray-100">
                    {row.map((cell, ci) => (
                      <td key={ci} className="px-3 py-2 text-gray-700 whitespace-nowrap">
                        {cell || <span className="text-gray-300">-</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {preview.rows.length > 5 && (
            <p className="text-xs text-gray-500 mt-1">
              ... dhe {preview.rows.length - 5} rreshta te tjere
            </p>
          )}
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={handleContinue}
          disabled={!preview}
          className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {STRINGS.btnContinue}
        </button>
      </div>
    </div>
  );
}
